import { useCallback, useEffect, useState } from "react";
import {
  getObservabilitySummary,
  hasCyclesInWindow,
  RETENTION_METRIC_WINDOW,
  type CollectionSummaryResp,
  type MetricWindow,
} from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset/loadState";

/**
 * 창 하나를 쓰는 지표 조회의 공통 골격이다.
 *
 * 폴링하지 않는다. 수집 주기가 5분이라 화면이 초 단위로 다시 조회해도 같은 스냅샷을 받는다.
 * 사용자가 새로고침해서 갱신한다.
 *
 * 다시 조회하는 동안 직전 데이터를 유지한다. 창을 바꿀 때마다 화면이 비었다가 다시 채워지면,
 * 값을 눈으로 대조하던 사용자가 매번 위치를 잃는다. 단 **`fetcher` 가 바뀌면 유지하지 않는다**.
 * 그때는 같은 지표의 다른 구간이 아니라 완전히 다른 응답이다. 그래서 이전 데이터가 새 소비처에 맞지 않는다.
 *
 * `fetcher` 는 모듈 함수처럼 **참조가 안정적이어야 한다**. 인라인 화살표 함수를 전달하면 매 렌더
 * 다른 조회로 판단해 다시 조회한다.
 */
export type MetricController<T> = {
  state: LoadState<T>;
  reload: () => void;
};

export function useObservabilityMetric<T>(
  fetcher: (window: MetricWindow) => Promise<T>,
  window: MetricWindow,
): MetricController<T> {
  const [state, setState] = useState<LoadState<T>>({ status: "idle" });
  const [reloadToken, setReloadToken] = useState(0);
  const [source, setSource] = useState(() => fetcher);

  // 조회 **대상**이 바뀌면 직전 데이터를 버린다. 창을 바꾼 경우와 달리 응답의 모양 자체가
  // 다르다. 두 hotspot 탭은 같은 패널을 같은 자리에서 쓴다. 그래서 탭을 옮겨도 이 훅은 계속
  // 유지된다. 이전 응답을 남겨 두면 새 탭의 행 변환기가 없는 필드를 읽어 화면이 멈춘다.
  // effect 는 이 렌더 **뒤**에 실행된다. 그래서 상태를 여기서 즉시 초기화해야 한다.
  if (source !== fetcher) {
    setSource(() => fetcher);
    setState({ status: "loading" });
  }

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setState((previous) => (previous.status === "success" ? previous : { status: "loading" }));
      try {
        const data = await fetcher(window);
        if (!cancelled) {
          setState({ status: "success", data });
        }
      } catch (error) {
        if (!cancelled) {
          setState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [fetcher, window, reloadToken]);

  return { state, reload };
}

/** 수집이 비어 있을 때 그 빈칸의 뜻이다. */
export type CollectionEmptiness =
  /** 아직 판정하지 못했다. 보존 구간 조회가 끝나지 않았다. */
  | "unknown"
  /** 보존 구간(30일) 전체에 기록이 없다. 수집기가 실행되지 않고 있다. */
  | "collectorIdle"
  /** 수집기는 실행되지만 이 창에는 사이클이 없다. */
  | "windowEmpty";

export type CollectionController = {
  state: LoadState<CollectionSummaryResp>;
  /** `state` 가 success 이고 이 창에 사이클이 없을 때만 의미가 있다. */
  emptiness: CollectionEmptiness;
  reload: () => void;
};

/**
 * 수집 건강을 조회한다. 빈 응답의 **뜻**까지 구분한다.
 *
 * `summary` 응답의 `cycles` 와 `last_captured_at` 은 모두 창 범위로 잘려서 온다. 그래서 한 번의
 * 조회로는 "수집기가 아직 실행되지 않는다" 와 "이 창에만 사이클이 없다" 를 구분할 수 없다.
 * 최근 1시간 동안 수집이 잠시 멈췄을 뿐인데 "수집기 미실행" 으로 표시하면 없는 장애를 만든다.
 *
 * 그래서 창이 비었을 때만 보존 구간 전체(`30d`)를 한 번 더 조회한다. 보존 기간이 30일이라 그 창이
 * "남아 있는 모든 기록" 이다. 추가 조회는 빈 경우에만 보내므로 정상 상태의 비용은 0 이다.
 */
export function useCollectionSummary(window: MetricWindow): CollectionController {
  const { state, reload } = useObservabilityMetric(getObservabilitySummary, window);
  const [emptiness, setEmptiness] = useState<CollectionEmptiness>("unknown");

  useEffect(() => {
    if (state.status !== "success" || hasCyclesInWindow(state.data)) {
      setEmptiness("unknown");
      return;
    }
    if (window === RETENTION_METRIC_WINDOW) {
      setEmptiness("collectorIdle");
      return;
    }
    let cancelled = false;
    setEmptiness("unknown");
    void getObservabilitySummary(RETENTION_METRIC_WINDOW)
      .then((retention) => {
        if (!cancelled) {
          setEmptiness(hasCyclesInWindow(retention) ? "windowEmpty" : "collectorIdle");
        }
      })
      .catch(() => {
        // 보존 구간 조회가 실패하면 두 경우 중 어느 쪽인지 알 수 없다. 그 상태로 둔다. 화면은
        // "이 구간에 사이클이 없다" 는 사실만 표시하고 원인을 단정하지 않는다.
        if (!cancelled) {
          setEmptiness("unknown");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [state, window]);

  return { state, emptiness, reload };
}
